import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '@udbodhanam/shared';
import apiClient from '../services/apiClient';
import { useAuthStore } from '../stores/useAuthStore';
import LanguageSelector from '../components/UI/LanguageSelector';
import ThemeToggle from '../components/UI/ThemeToggle';

const ROLE_HOME: Record<string, string> = {
  ADMIN: '/admin',
  MENTOR: '/mentor',
  STUDENT: '/student',
};

export default function Consent() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user, role, consentStatus, mustChangePassword, isFirstLogin, setAuth } = useAuthStore();

  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Minor users cannot self-consent
  if (consentStatus === 'MINOR_PENDING') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-4">
        <div className="w-full max-w-md bg-surface border border-border rounded-xl p-[22px] text-center space-y-4">
          <h1 className="text-[22px] leading-7 font-semibold text-earth-800">{t('consent.minorPendingTitle')}</h1>
          <p className="text-sm text-earth-600">{t('consent.minorPendingMessage')}</p>
        </div>
      </main>
    );
  }

  async function handleAccept() {
    setError('');
    setLoading(true);
    try {
      await apiClient.post('/auth/consent');
      if (user && role) {
        setAuth({ ...user }, role, 'GRANTED', mustChangePassword, isFirstLogin);
      }
      navigate(ROLE_HOME[role ?? ''] ?? '/login', { replace: true });
    } catch {
      setError('consent.failedConsent');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-surface px-4 py-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Controls */}
        <div className="flex justify-end gap-2">
          <ThemeToggle />
          <LanguageSelector />
        </div>
        {/* Header */}
        <div>
          <p className="text-sm font-medium text-primary-600 mb-1">{APP_NAME}</p>
          <h1 className="text-[22px] leading-7 font-semibold text-earth-800 tracking-[-0.01em]">
            {t('consent.title')}
          </h1>
          <p className="mt-2 text-sm text-earth-600">{t('consent.intro')}</p>
        </div>

        {/* Two-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 items-start">
          {/* Left — scrollable privacy text */}
          <div className="bg-surface border border-border rounded-xl p-[22px] max-h-[420px] overflow-y-auto space-y-4 text-sm text-earth-700">
            <section>
              <h2 className="font-semibold text-earth-800 mb-1">{t('consent.dataCollected')}</h2>
              <p>{t('consent.dataCollectedBody')}</p>
            </section>
            <section>
              <h2 className="font-semibold text-earth-800 mb-1">{t('consent.dataUsed')}</h2>
              <p>{t('consent.dataUsedBody')}</p>
            </section>
            <section>
              <h2 className="font-semibold text-earth-800 mb-1">{t('consent.yourRights')}</h2>
              <p>{t('consent.yourRightsBody')}</p>
            </section>
            <section>
              <h2 className="font-semibold text-earth-800 mb-1">{t('consent.retention')}</h2>
              <p>{t('consent.retentionBody')}</p>
            </section>
            <section>
              <h2 className="font-semibold text-earth-800 mb-1">{t('consent.contact')}</h2>
              <p>privacy@udbodhanam.in</p>
            </section>
          </div>

          {/* Right — consent card */}
          <div className="bg-surface border border-border rounded-xl shadow-card p-[22px] space-y-4">
            {/* DPDP callout */}
            <div className="bg-viveka-50 border border-viveka-200 rounded-lg p-3 text-viveka-800 text-sm flex items-start gap-2">
              <span className="shrink-0 mt-0.5">🔒</span>
              <span>{t('consent.dpdpCallout')}</span>
            </div>

            {/* Consent checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="sr-only"
              />
              <span
                className={`w-[18px] h-[18px] rounded border-2 inline-grid place-items-center flex-shrink-0 mt-0.5
                  ${checked ? 'border-primary-500 bg-primary-500 text-white' : 'border-earth-300 bg-surface'}`}
              >
                {checked && <span className="text-[12px] font-bold leading-none">✓</span>}
              </span>
              <span className="text-sm text-earth-700">
                {t('consent.checkboxLabel')} <span className="text-error">*</span>
              </span>
            </label>

            {error !== '' && <p className="text-sm text-error">{t(error)}</p>}

            <button
              type="button"
              onClick={handleAccept}
              disabled={!checked || loading}
              className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('consent.processing') : t('consent.acceptButton')}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
